<template lang="pug">
#recruitment-contact-list(v-cloak)
  .box
    .box-header
      .d-flex.justify-content-start.align-items-center
        .d-flex.justify-content-start.flex-column
          .title
            span 入所募集問い合わせ一覧
        .d-flex.justify-content-end.align-items-start.align-self-stretch.flex-fill
          button.btn.register-btn(type="button" @click="gotoRegister") 代理登録
      .error-messages
        .error-message {{ errorMessage }}

    .box-body
      RecruitmentContactTable(
        v-model="items"
        :fields="fields"
        :totalRows.sync="rows"
        :currentPage="currentPage"
        :perPage="perPage"
        :isLoading="isLoading"
        :filter="filter"
        @onChangeDone="onChangeDone"
        @onEdit="onEdit"
        @onDelete="onDelete"
      )
      .table-options.d-flex.flex-row.justify-content-end.align-items-center.flex-wrap
        // 検索
        .search-filter.d-flex.mr-1.align-items-center.mr-2
          b-input-group.search-filter-input-group.mr-1(size="md")
            b-input-group-prepend
              b-input-group-text.search-filter-icon
                .search-icon
            b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
          HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="search-filter-help" title="検索項目について")
            template(v-slot:text)
              div 検索項目は以下になります。
              div ・氏名
              div ・申込者からの連絡事項
              div ・受付メモ
        // ページあたりの行数
        span.table-option-text 1ページの行数:
        .per-page
          b-form-select(
            id="per-page-select"
            v-model="perPage"
            :options="pageOptions"
            size="md"
            @change="selectPerPage"
          )
        // ページネーション
        b-pagination.m-0(
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          :disabled="isLoading"
          @change="selectPage"
          hide-goto-end-buttons
        )

  OkCancelModal(v-model="showDeleteConfirmModal" title="入所募集問い合わせを削除しますか？" @ok="deleteRecruitmentContact(recruitmentContactsSortKey)")
  OkModal(v-model="showDeleteSuccessModal" title="削除に成功しました。")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";

import Setting from "../../../../public/setting.json";
import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";

import RecruitmentContactsService, { RecruitmentContactsGetModel, RecruitmentContactsPatchModel } from "@/commons/services/RecruitmentContacts";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import RecruitmentContactTable, { RecruitmentContactTableField, RecruitmentContactTableItem, FilterSetting } from "@/pc/containers/recruitmentContacts/RecruitmentContactTable.vue";

/**
 * ### 入所募集問い合わせ一覧画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkCancelModal,
    OkModal,
    HelpIcon,
    RecruitmentContactTable
  }
})
export default class RecruitmentContactList extends Vue {
  /** 入所募集問い合わせサービス */
  @Inject("RecruitmentContactsService") recruitmentContactsService!: RecruitmentContactsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** エラーメッセージ */
  errorMessage: string = "";

  /** 削除確認モーダル表示可否 */
  showDeleteConfirmModal: boolean = false;
  /** 削除完了モーダル表示可否 */
  showDeleteSuccessModal: boolean = false;

  /** 削除対象の入所募集問い合わせ申込み日 */
  recruitmentContactsSortKey: string | null = null;

  /** 列名 */
  fields: Array<RecruitmentContactTableField> = [];
  /** 行 */
  items: Array<RecruitmentContactTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;
  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "" };
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.recruitmentContacts.recruitmentContactList.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.recruitmentContacts.recruitmentContactList.pageOptions;

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);

    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `recruitment-contact-list-per-page=${perPage}; max-age=${saveSecond};`;

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<RecruitmentContactTableField> = this.createFields();
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.recruitmentContacts.recruitmentContactList.perPage);

    // 動的に変更する画面情報を作成する
    this.items = [];
    this.currentRows = this.items.length;

    await this.createView();

    return;
  }

  /**
   * ### 列名を作成する
   */
  public createFields(): Array<RecruitmentContactTableField> {
    let fields: Array<RecruitmentContactTableField> = [];

    fields.push({ key: "done", label: "対応", sortable: false, sortDirection: "desc", class: "done text-center" });
    fields.push({ key: "contactType", label: "登録者", sortable: false, sortDirection: "desc", class: "contact-type text-center" });
    fields.push({ key: "contactDatetime", label: "お問い合わせ日時", sortable: false, sortDirection: "desc", class: "contact-datetime" });
    fields.push({ key: "nameAndTel", label: "氏名／電話番号", sortable: false, sortDirection: "desc", class: "name-and-tel" });
    fields.push({ key: "contactMessage", label: "申込者からの連絡事項", sortable: false, sortDirection: "desc", class: "contact-message" });
    fields.push({ key: "supportMemo", label: "対応メモ", sortable: false, sortDirection: "desc", class: "support-memo" });
    fields.push({ key: "operation", label: "操作", sortable: false, sortDirection: "desc", class: "operation text-center" });

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["recruitment-contact-list-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    // 全体の行数を非同期で取得する
    this.setRows();

    // 家族テーブルに渡す各プロパティを作成する
    const records: Array<RecruitmentContactsGetModel> = await this.getRecruitmentContacts();
    const items: Array<RecruitmentContactTableItem> = this.createItems(records);
    this.items = items;
    this.currentRows = this.items.length;

    this.isLoading = false;

    // サイドメニューを更新
    this.$emit("refresh");

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows() {
    try {
      const response: Array<RecruitmentContactsGetModel> = await this.recruitmentContactsService.gets(userStore);
      this.rows = response.length;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### 入所募集問い合わせを取得する
   */
  public async getRecruitmentContacts(): Promise<Array<RecruitmentContactsGetModel>> {
    const response: Array<RecruitmentContactsGetModel> = await this.recruitmentContactsService.gets(userStore);
    return response;
  }

  /**
   * ### 行を作成する
   */
  public createItems(records: Array<RecruitmentContactsGetModel>): Array<RecruitmentContactTableItem> {
    let items: Array<RecruitmentContactTableItem> = [];

    for (const record of records) {
      items.push({
        ...record
      });
    }

    return items;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    this.items = [];
    this.currentRows = this.items.length;
    await this.createView();

    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  /**
   * ### 代理登録画面へ遷移する
   */
  public gotoRegister(): void {
    this.$router.push(PcUrl.RecruitmentContactRegister);
    return;
  }

  /**
   * ### 対応状態を更新する
   */
  public async onChangeDone(item: RecruitmentContactTableItem): Promise<void> {
    try {
      this.isBusy = true;
      this.loadingMessage = "更新中...";

      const requestBody: RecruitmentContactsPatchModel = this.createChangeDoneRequestBody(item);
      await this.recruitmentContactsService.patch(userStore, item.recruitmentContactsSortKey, requestBody);

      // ページ表示をリフレッシュする
      this.items = [];
      this.currentRows = this.items.length;
      this.currentPage = 1;
      await this.createView();
    } catch (error) {
      const systemError: SystemError = error as SystemError;

      if (systemError.statusCode == 409) {
        this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 対応状態の変更リクエストボディを作成する
   */
  public createChangeDoneRequestBody(item: RecruitmentContactTableItem): RecruitmentContactsPatchModel {
    return {
      done: item.done, // 対応状態は子コンポーネントで値更新しているのでそのままAPI呼び出しして更新する
      customerName: item.customerName,
      supportMemo: item.supportMemo,
      updated: item.updated
    };
  }

  /**
   * ### 入所募集問い合わせ編集画面へ遷移する
   */
  public onEdit(item: RecruitmentContactTableItem): void {
    this.$router.push({ name: PcUrl.RecruitmentContactEdit, params: { datetime: `${item.recruitmentContactsSortKey}` } });
    return;
  }

  /**
   * ### 入所募集問い合わせの削除確認モーダルを表示する
   */
  public onDelete(item: RecruitmentContactTableItem): void {
    this.recruitmentContactsSortKey = item.recruitmentContactsSortKey;
    this.showDeleteConfirmModal = true;
    return;
  }

  /**
   * ### 入所募集問い合わせを削除する
   */
  public async deleteRecruitmentContact(recruitmentContactsSortKey: string): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    await this.recruitmentContactsService.delete(userStore, recruitmentContactsSortKey);

    this.showDeleteSuccessModal = true;
    this.isBusy = false;

    // ページ表示をリフレッシュする
    this.items = [];
    this.currentRows = this.items.length;
    this.currentPage = 1;
    await this.createView();

    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

#recruitment-contact-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .box-header {
    .title {
      font-size: 22px;
      font-weight: bold;
    }
  }
}
</style>
